import React from 'react'
import { Helmet } from 'react-helmet'
import PropTypes from 'prop-types'

const BASE_URL = 'https://goleevo.com'

export function OrganizationSchema() {
  const schema = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: 'Leevo',
    url: BASE_URL,
    logo: `${BASE_URL}/favicons/production/favicon192.png`,
    description:
      'Leevo is the smarter way to run your sports program. Manage classes, registrations, and payments with powerful sports management software.',
    sameAs: ['https://www.linkedin.com/company/leevo'],
    contactPoint: {
      '@type': 'ContactPoint',
      contactType: 'customer service',
      availableLanguage: 'English'
    },
    knowsAbout: [
      'Sports program management',
      'Class registration software',
      'Ice skating software',
      'Gymnastics class management',
      'Swimming lesson software',
      'Dance studio software',
      'Martial arts school management',
      'Youth sports programs',
      'Skill tracking',
      'Online registration'
    ]
  }

  return (
    <Helmet>
      <script type="application/ld+json">{JSON.stringify(schema)}</script>
    </Helmet>
  )
}

export function WebSiteSchema() {
  const schema = {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    name: 'Leevo',
    url: BASE_URL,
    description: 'Sports program management software for ice skating, gymnastics, swimming, dance, martial arts, and more',
    potentialAction: {
      '@type': 'SearchAction',
      target: {
        '@type': 'EntryPoint',
        urlTemplate: `${BASE_URL}/blog?q={search_term_string}`
      },
      'query-input': 'required name=search_term_string'
    }
  }

  return (
    <Helmet>
      <script type="application/ld+json">{JSON.stringify(schema)}</script>
    </Helmet>
  )
}

export function SoftwareApplicationSchema() {
  const schema = {
    '@context': 'https://schema.org',
    '@type': 'SoftwareApplication',
    name: 'Leevo',
    applicationCategory: 'BusinessApplication',
    applicationSubCategory: 'Sports Management Software',
    operatingSystem: 'Web',
    description:
      'Sports program management software for class scheduling, student registration, payment processing, and skill tracking. Perfect for ice skating rinks, gymnastics gyms, swim schools, dance studios, and martial arts dojos.',
    url: BASE_URL,
    offers: {
      '@type': 'Offer',
      price: '0',
      priceCurrency: 'USD',
      description: '30-day free trial available'
    },
    featureList: [
      'Class scheduling and management',
      'Online student registration',
      'Payment processing with Stripe',
      'Skill tracking and progression',
      'Parent communication tools',
      'Multi-sport curriculum support',
      'Automated billing',
      'Student progress reports',
      'Mobile-friendly interface',
      'Real-time attendance tracking'
    ],
    screenshot: `${BASE_URL}/og-image.png`,
    softwareVersion: '2.0',
    aggregateRating: {
      '@type': 'AggregateRating',
      ratingValue: '4.9',
      ratingCount: '150',
      bestRating: '5',
      worstRating: '1'
    }
  }

  return (
    <Helmet>
      <script type="application/ld+json">{JSON.stringify(schema)}</script>
    </Helmet>
  )
}

export function FAQPageSchema({ faqs }) {
  const schema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqs.map((faq) => ({
      '@type': 'Question',
      name: faq.question,
      acceptedAnswer: {
        '@type': 'Answer',
        text: faq.answer
      }
    }))
  }

  return (
    <Helmet>
      <script type="application/ld+json">{JSON.stringify(schema)}</script>
    </Helmet>
  )
}

FAQPageSchema.propTypes = {
  faqs: PropTypes.arrayOf(
    PropTypes.shape({
      question: PropTypes.string.isRequired,
      answer: PropTypes.string.isRequired
    })
  ).isRequired
}

export function BreadcrumbSchema({ items }) {
  const schema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: items.map((item, index) => ({
      '@type': 'ListItem',
      position: index + 1,
      name: item.name,
      item: `${BASE_URL}${item.path}`
    }))
  }

  return (
    <Helmet>
      <script type="application/ld+json">{JSON.stringify(schema)}</script>
    </Helmet>
  )
}

BreadcrumbSchema.propTypes = {
  items: PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.string.isRequired,
      path: PropTypes.string.isRequired
    })
  ).isRequired
}

export function ArticleSchema({ title, description, path, datePublished, dateModified, keywords = [] }) {
  const schema = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: title,
    description: description,
    url: `${BASE_URL}${path}`,
    datePublished: datePublished,
    dateModified: dateModified || datePublished,
    author: {
      '@type': 'Organization',
      name: 'Leevo',
      url: BASE_URL
    },
    publisher: {
      '@type': 'Organization',
      name: 'Leevo',
      logo: {
        '@type': 'ImageObject',
        url: `${BASE_URL}/favicons/production/favicon192.png`
      }
    },
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': `${BASE_URL}${path}`
    },
    keywords: keywords.join(', ')
  }

  return (
    <Helmet>
      <script type="application/ld+json">{JSON.stringify(schema)}</script>
    </Helmet>
  )
}

ArticleSchema.propTypes = {
  title: PropTypes.string.isRequired,
  description: PropTypes.string.isRequired,
  path: PropTypes.string.isRequired,
  datePublished: PropTypes.string.isRequired,
  dateModified: PropTypes.string,
  keywords: PropTypes.arrayOf(PropTypes.string)
}

export function ServiceSchema({ sportName, sportSlug, description, features = [] }) {
  const schema = {
    '@context': 'https://schema.org',
    '@type': 'Service',
    name: `${sportName} Program Management Software`,
    description: description,
    url: `${BASE_URL}/sports/${sportSlug}`,
    provider: {
      '@type': 'Organization',
      name: 'Leevo',
      url: BASE_URL
    },
    serviceType: 'Sports Management Software',
    areaServed: {
      '@type': 'Country',
      name: 'United States'
    },
    hasOfferCatalog: {
      '@type': 'OfferCatalog',
      name: `${sportName} Software Features`,
      itemListElement: features.map((feature, index) => ({
        '@type': 'Offer',
        itemOffered: {
          '@type': 'Service',
          name: feature
        },
        position: index + 1
      }))
    }
  }

  return (
    <Helmet>
      <script type="application/ld+json">{JSON.stringify(schema)}</script>
    </Helmet>
  )
}

ServiceSchema.propTypes = {
  sportName: PropTypes.string.isRequired,
  sportSlug: PropTypes.string.isRequired,
  description: PropTypes.string.isRequired,
  features: PropTypes.arrayOf(PropTypes.string)
}

export function SpeakableSchema({ path, cssSelectors = [] }) {
  const schema = {
    '@context': 'https://schema.org',
    '@type': 'WebPage',
    url: `${BASE_URL}${path}`,
    speakable: {
      '@type': 'SpeakableSpecification',
      cssSelector: cssSelectors.length > 0 ? cssSelectors : ['h1', '.hero-description', '.key-features']
    }
  }

  return (
    <Helmet>
      <script type="application/ld+json">{JSON.stringify(schema)}</script>
    </Helmet>
  )
}

SpeakableSchema.propTypes = {
  path: PropTypes.string.isRequired,
  cssSelectors: PropTypes.arrayOf(PropTypes.string)
}

export function HowToSchema({ title, description, steps, totalTime }) {
  const schema = {
    '@context': 'https://schema.org',
    '@type': 'HowTo',
    name: title,
    description: description,
    totalTime: totalTime,
    step: steps.map((step, index) => ({
      '@type': 'HowToStep',
      position: index + 1,
      name: step.name,
      text: step.text,
      url: step.url ? `${BASE_URL}${step.url}` : undefined
    }))
  }

  return (
    <Helmet>
      <script type="application/ld+json">{JSON.stringify(schema)}</script>
    </Helmet>
  )
}

HowToSchema.propTypes = {
  title: PropTypes.string.isRequired,
  description: PropTypes.string.isRequired,
  steps: PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.string.isRequired,
      text: PropTypes.string.isRequired,
      url: PropTypes.string
    })
  ).isRequired,
  totalTime: PropTypes.string
}

export function VideoObjectSchema({ title, description, thumbnailUrl, uploadDate, duration, contentUrl, embedUrl }) {
  const schema = {
    '@context': 'https://schema.org',
    '@type': 'VideoObject',
    name: title,
    description: description,
    thumbnailUrl: thumbnailUrl,
    uploadDate: uploadDate,
    duration: duration,
    contentUrl: contentUrl,
    embedUrl: embedUrl,
    publisher: {
      '@type': 'Organization',
      name: 'Leevo',
      logo: {
        '@type': 'ImageObject',
        url: `${BASE_URL}/favicons/production/favicon192.png`
      }
    }
  }

  return (
    <Helmet>
      <script type="application/ld+json">{JSON.stringify(schema)}</script>
    </Helmet>
  )
}

VideoObjectSchema.propTypes = {
  title: PropTypes.string.isRequired,
  description: PropTypes.string.isRequired,
  thumbnailUrl: PropTypes.string.isRequired,
  uploadDate: PropTypes.string.isRequired,
  duration: PropTypes.string,
  contentUrl: PropTypes.string,
  embedUrl: PropTypes.string
}

export function ItemListSchema({ name, description, items }) {
  const schema = {
    '@context': 'https://schema.org',
    '@type': 'ItemList',
    name: name,
    description: description,
    numberOfItems: items.length,
    itemListElement: items.map((item, index) => ({
      '@type': 'ListItem',
      position: index + 1,
      name: item.name,
      description: item.description,
      url: item.url ? `${BASE_URL}${item.url}` : undefined
    }))
  }

  return (
    <Helmet>
      <script type="application/ld+json">{JSON.stringify(schema)}</script>
    </Helmet>
  )
}

ItemListSchema.propTypes = {
  name: PropTypes.string.isRequired,
  description: PropTypes.string,
  items: PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.string.isRequired,
      description: PropTypes.string,
      url: PropTypes.string
    })
  ).isRequired
}

export function LocalBusinessSchema({ facilityName, address, phone, url }) {
  const schema = {
    '@context': 'https://schema.org',
    '@type': 'SportsActivityLocation',
    name: facilityName,
    address: {
      '@type': 'PostalAddress',
      streetAddress: address.street,
      addressLocality: address.city,
      addressRegion: address.state,
      postalCode: address.zip,
      addressCountry: 'US'
    },
    telephone: phone,
    url: url,
    potentialAction: {
      '@type': 'ReserveAction',
      target: {
        '@type': 'EntryPoint',
        urlTemplate: url,
        actionPlatform: ['http://schema.org/DesktopWebPlatform', 'http://schema.org/MobileWebPlatform']
      },
      result: {
        '@type': 'Reservation',
        name: 'Class Registration'
      }
    }
  }

  return (
    <Helmet>
      <script type="application/ld+json">{JSON.stringify(schema)}</script>
    </Helmet>
  )
}

LocalBusinessSchema.propTypes = {
  facilityName: PropTypes.string.isRequired,
  address: PropTypes.shape({
    street: PropTypes.string,
    city: PropTypes.string,
    state: PropTypes.string,
    zip: PropTypes.string
  }).isRequired,
  phone: PropTypes.string,
  url: PropTypes.string.isRequired
}

